import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface LibraryPageProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

const TABS = [
  { key: 'CURRENTLY_READING', label: 'Currently Reading', icon: '📖' },
  { key: 'WANT_TO_READ', label: 'Want to Read', icon: '📚' },
  { key: 'COMPLETED', label: 'Completed', icon: '✅' },
  { key: 'DROPPED', label: 'Dropped', icon: '🚫' },
  { key: 'BOOKMARKS', label: 'Bookmarks', icon: '🔖' },
];

export default function LibraryPage({ onNavigate, user }: LibraryPageProps) {
  const [lists, setLists] = useState<any>(null);
  const [bookmarks, setBookmarks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('CURRENTLY_READING');

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.users.readingLists(),
      api.users.bookmarks(),
    ])
      .then(([listsData, bmData]) => {
        setLists(listsData);
        setBookmarks(bmData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Sign in to view your library</h2>
        <button onClick={() => onNavigate('login')} className="px-6 py-3 bg-ink-600 rounded-xl font-semibold">
          Sign In
        </button>
      </div>
    );
  }

  const currentList = activeTab === 'BOOKMARKS' ? bookmarks : (lists?.[activeTab] || []);

  const getTabCount = (key: string) => {
    if (key === 'BOOKMARKS') return bookmarks.length;
    return lists?.[key]?.length || 0;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">My Library</h1>

      <div className="flex gap-2 flex-wrap mb-6">
        {TABS.map(tab => {
          const count = getTabCount(tab.key);
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-ink-600 text-white'
                  : 'bg-gray-900 text-gray-400 border border-gray-800 hover:border-ink-500/50'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeTab === tab.key ? 'bg-white/20' : 'bg-gray-800'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activeTab === 'BOOKMARKS' ? (
        bookmarks.length > 0 ? (
          <div className="space-y-3">
            {bookmarks.map((bm: any) => (
              <div
                key={bm.chapterId}
                onClick={() => onNavigate('reader', { storySlug: bm.chapter.story.slug, chapterId: bm.chapter.id })}
                className="flex items-start gap-4 p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-ink-500/50 cursor-pointer transition-all"
              >
                {bm.chapter.story.coverUrl ? (
                  <img
                    src={bm.chapter.story.coverUrl}
                    alt={bm.chapter.story.title}
                    className="w-16 h-22 object-cover rounded-lg shrink-0"
                  />
                ) : (
                  <div className="w-16 h-22 bg-gradient-to-br from-ink-600 to-purple-700 rounded-lg shrink-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-center px-1 leading-tight">{bm.chapter.story.title.slice(0, 20)}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white truncate">{bm.chapter.story.title}</h3>
                  <p className="text-sm text-gray-400">
                    by {bm.chapter.story.author?.username || bm.chapter.story.penName?.penName || 'Unknown'}
                  </p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-amber-400 font-medium">🔖 Chapter {bm.chapter.chapterNum}: {bm.chapter.title}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-600 shrink-0">
                  Bookmarked {new Date(bm.createdAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-gray-900/50 rounded-2xl border border-gray-800">
            <span className="text-5xl block mb-4">🔖</span>
            <h2 className="text-xl font-bold mb-2">No bookmarks yet</h2>
            <p className="text-gray-400 mb-6">
              While reading a chapter, tap the bookmark icon to save your place and come back to it anytime.
            </p>
            <button
              onClick={() => onNavigate('discover')}
              className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
            >
              Discover Stories
            </button>
          </div>
        )
      ) : currentList.length > 0 ? (
        <div className="space-y-3">
          {currentList.map((item: any) => (
            <div
              key={item.id}
              onClick={() => onNavigate('story', { id: item.story.id, slug: item.story.slug })}
              className="flex items-start gap-4 p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-ink-500/50 cursor-pointer transition-all"
            >
              {item.story.coverUrl ? (
                <img
                  src={item.story.coverUrl}
                  alt={item.story.title}
                  className="w-16 h-22 object-cover rounded-lg shrink-0"
                />
              ) : (
                <div className="w-16 h-22 bg-gradient-to-br from-ink-600 to-purple-700 rounded-lg shrink-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-center px-1 leading-tight">{item.story.title.slice(0, 20)}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-white truncate">{item.story.title}</h3>
                <p className="text-sm text-gray-400">
                  by {item.story.author?.username || 'Unknown'}
                </p>
                <div className="flex gap-3 mt-1.5 text-xs text-gray-500">
                  <span>{item.story.genre}</span>
                  <span>{item.story._count?.chapters || 0} chapters</span>
                  <span>{item.story._count?.follows || 0} followers</span>
                  <span className={`${
                    item.story.status === 'ONGOING' ? 'text-emerald-500'
                    : item.story.status === 'COMPLETED' ? 'text-blue-400'
                    : 'text-amber-400'
                  }`}>
                    {item.story.status}
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-600 shrink-0">
                Added {new Date(item.createdAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 bg-gray-900/50 rounded-2xl border border-gray-800">
          <span className="text-5xl block mb-4">
            {TABS.find(t => t.key === activeTab)?.icon || '📚'}
          </span>
          <h2 className="text-xl font-bold mb-2">No stories here yet</h2>
          <p className="text-gray-400 mb-6">
            {activeTab === 'CURRENTLY_READING' && 'Start reading a story and add it to your library!'}
            {activeTab === 'WANT_TO_READ' && 'Browse stories and save ones you want to read later.'}
            {activeTab === 'COMPLETED' && 'Stories you finish will appear here.'}
            {activeTab === 'DROPPED' && 'Stories you stop reading will show up here.'}
          </p>
          <button
            onClick={() => onNavigate('discover')}
            className="px-6 py-3 bg-ink-600 hover:bg-ink-500 rounded-xl font-semibold transition-colors"
          >
            Discover Stories
          </button>
        </div>
      )}
    </div>
  );
}
